// src/components/layout/Navbar.jsx
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { supabase } from '../../lib/supabase'

const { FiHome, FiLogOut, FiUser, FiBookOpen, FiActivity } = FiIcons

const Navbar = () => {
  const { user, userProfile, signOut } = useAuth()
  const navigate = useNavigate()

  const [usage, setUsage] = useState(null)
  const [usageLoading, setUsageLoading] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  useEffect(() => {
    if (!user) return

    const fetchUsage = async () => {
      try {
        setUsageLoading(true)

        // First day of current month in UTC, 'YYYY-MM-DD'
        const now = new Date()
        const periodStart = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
        )
          .toISOString()
          .slice(0, 10)

        const { data, error } = await supabase
          .from('user_ai_usage')
          .select('token_count, token_limit')
          .eq('period_start', periodStart)
          .maybeSingle()

        if (error) {
          console.error('Error loading AI usage in Navbar:', error)
          // Fallback: no usage info
          setUsage(null)
          return
        }

        if (!data) {
          // No row yet this period → treat as 0 used, default limit
          setUsage({
            tokenCount: 0,
            tokenLimit: 1000000, // keep in sync with server default
          })
          return
        }

        setUsage({
          tokenCount: data.token_count ?? 0,
          tokenLimit: data.token_limit ?? 1000000,
        })
      } catch (e) {
        console.error('Unexpected error loading AI usage in Navbar:', e)
        setUsage(null)
      } finally {
        setUsageLoading(false)
      }
    }

    fetchUsage()
  }, [user])

  if (!user) return null

  const formatTokens = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
    return String(n)
  }

  const usagePercent =
    usage && usage.tokenLimit > 0
      ? Math.min(100, Math.round((usage.tokenCount / usage.tokenLimit) * 100))
      : 0

  // Subtle colour shift as they get closer to the limit
  const usageBarColor =
    usagePercent >= 90
      ? 'bg-red-400'
      : usagePercent >= 70
      ? 'bg-amber-400'
      : 'bg-emerald-400'

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left: App name + nav links */}
          <div className="flex items-center space-x-8">
            <Link to="/dashboard" className="text-xl font-bold text-gray-900">
              Lesson Planner
            </Link>

            <Link
              to="/dashboard"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiHome} className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>

            <Link
              to="/pedagogy"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiBookOpen} className="w-4 h-4" />
              <span>Pedagogy Library</span>
            </Link>
          </div>

          {/* Right: AI usage + user + sign out */}
          <div className="flex items-center space-x-4">
            {/* 🔹 Subtle AI usage indicator */}
            {usage && (
              <div
                className="hidden sm:flex items-center space-x-2 text-xs text-gray-500"
                title={`AI usage this month: ${formatTokens(
                  usage.tokenCount,
                )} / ${formatTokens(
                  usage.tokenLimit,
                )} tokens (${usagePercent}%)`}
              >
                <SafeIcon icon={FiActivity} className="w-4 h-4" />
                <div className="flex flex-col">
                  <span className="leading-tight">
                    AI usage: {usagePercent}%
                  </span>
                  <div className="mt-0.5 w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${usageBarColor} rounded-full transition-all`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2 text-gray-600">
              <SafeIcon icon={FiUser} className="w-4 h-4" />
              <span className="text-sm">
                {userProfile?.email || user.email}
              </span>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar